import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Input,
  Sheet,
  Skeleton,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { Link as RouterLink } from "react-router-dom";

import {
  fetchAdminAnnouncements,
  searchAdminAnnouncements,
  AdminAnnouncement,
} from "../../api";

export default function AdminAnnouncementsList() {
  const [announcements, setAnnouncements] = useState<AdminAnnouncement[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const summary = React.useMemo(() => {
    const total = announcements.length;
    const recent = announcements.filter((item) => {
      if (!item.created_at) return false;
      const created = new Date(item.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return created >= weekAgo;
    }).length;
    return { total, recent };
  }, [announcements]);

  useEffect(() => {
    setLoading(true);
    const load = query ? searchAdminAnnouncements(query) : fetchAdminAnnouncements();
    load
      .then((data) => setAnnouncements(data ?? []))
      .finally(() => setLoading(false));
  }, [query]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        boxShadow: "sm",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          <Typography level="title-lg" sx={{ fontWeight: 700 }}>
            Message center
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Craft uplifting notes and track how devotees are engaging with your updates.
          </Typography>
        </Stack>
        <Chip size="sm" variant="soft" color="primary">
          {announcements.length} messages
        </Chip>
      </Stack>

      <Sheet
        variant="soft"
        sx={{
          borderRadius: "lg",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.level1",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <Input
            placeholder="Search announcements"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            startDecorator={<SearchRoundedIcon fontSize="small" />}
            sx={{ maxWidth: 360, bgcolor: "background.surface" }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="sm" variant="soft" color="primary">
              {summary.total} total
            </Chip>
            <Chip size="sm" variant="soft" color="success">
              {summary.recent} this week
            </Chip>
          </Stack>
        </Stack>
      </Sheet>

      <Sheet
        variant="outlined"
        sx={{ borderRadius: "xl", overflowX: "auto", overflowY: "hidden", boxShadow: "sm", borderColor: "divider" }}
      >
        <Table
          hoverRow
          stickyHeader
          size="sm"
          stripe="odd"
          borderAxis="bothBetween"
          sx={{
            minWidth: 720,
            "--TableCell-headBackground": "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "12px",
            "--TableCell-paddingX": "16px",
            "& th": {
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.tertiary",
              fontWeight: 700,
            },
            "& td": {
              fontSize: "0.86rem",
            },
          }}
        >
          <thead>
            <tr>
              <th>Announcement</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={2}>
                      <Skeleton level="body-sm" />
                    </td>
                  </tr>
                ))
              : announcements.length === 0
              ? (
                  <tr>
                    <td colSpan={2}>
                      <Typography level="body-sm" sx={{ py: 3, textAlign: "center" }}>
                        No announcements found. Your next message can inspire new seva today.
                      </Typography>
                    </td>
                  </tr>
                )
              : (
                  announcements.map((announcement) => (
                    <tr key={announcement.id}>
                      <td>
                        <Stack spacing={0.5}>
                          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                            {announcement.title}
                          </Typography>
                          {announcement.created_at ? (
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                              Posted on {new Date(announcement.created_at).toLocaleString()}
                            </Typography>
                          ) : null}
                        </Stack>
                      </td>
                      <td>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            component={RouterLink}
                            to={`/announcements/${announcement.id}`}
                            size="sm"
                            startDecorator={<VisibilityRoundedIcon />}
                          >
                            View
                          </Button>
                        </Box>
                      </td>
                    </tr>
                  ))
                )}
          </tbody>
        </Table>
      </Sheet>
    </Card>
  );
}
